import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, UserRound, X } from "lucide-react";
import Logo from "./Logo";
import { useApp, scrollToId } from "../context/AppContext";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "aggregator", label: "CARZA Nearby" },
  { id: "business", label: "For Business" },
  { id: "partners", label: "Partners" },
  { id: "about", label: "About" },
];

const PAGE_LINKS = [
  { path: "/aggregator", label: "CARZA Nearby" },
  { path: "/investors", label: "For Investors" },
];

export default function Navbar() {
  const { openBooking, customer, signOut } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToId(id);
    }
  };

  const goPage = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-black/[0.06] bg-white/85 text-nighttext backdrop-blur-xl"
            : "border-b border-transparent bg-transparent text-paper"
        }`}
        data-testid="site-navbar"
      >
        <div className="container-x flex h-[68px] items-center justify-between gap-4">
          <button onClick={() => (location.pathname !== "/" ? navigate("/") : window.__lenis && window.__lenis.scrollTo(0))} aria-label="CARZA home">
            <Logo dark={scrolled} />
          </button>

          <nav className={`hidden items-center gap-7 lg:flex ${scrolled ? "text-nighttext/80" : "text-mist"}`} aria-label="Primary">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => (l.id === "aggregator" ? goPage("/aggregator") : goSection(l.id))}
                className="text-[13px] font-medium transition-colors hover:text-carza"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
                {l.id === "aggregator" && (
                  <span className="ml-1.5 inline-block rounded-full bg-carza/15 px-2 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-carza" data-testid="nav-nearby-soon-badge">
                    Soon
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => goPage("/investors")}
              className={`text-[13px] font-medium transition-colors ${scrolled ? "text-mist-2 hover:text-carza" : "text-mist-2 hover:text-carza-lite"}`}
              data-testid="nav-investors-link"
            >
              Investors
            </button>
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            {customer ? (
              <div className="flex items-center gap-2">
                <span className={`font-mono text-[11px] ${scrolled ? "text-nighttext/70" : "text-mist"}`} data-testid="nav-customer-name">
                  {customer.name || customer.phone}
                </span>
                <button onClick={signOut} className={`text-[12px] font-medium ${scrolled ? "text-mist-2" : "text-mist-2"}`} data-testid="nav-signout-btn">
                  Sign out
                </button>
              </div>
            ) : (
              <Link to="/login" className={`btn text-[13px] ${scrolled ? "text-nighttext hover:text-carza" : "text-paper hover:text-carza-lite"}`} data-testid="nav-signin-link">
                <UserRound size={14} /> Sign in
              </Link>
            )}
            <button
              onClick={() => goPage("/partners")}
              className={`btn text-[13px] ${scrolled ? "btn-outline-dark" : "btn-outline"}`}
              data-testid="nav-partner-btn"
            >
              Partner With Us
            </button>
            <button onClick={() => openBooking()} className="btn btn-primary px-5 py-2.5 text-[13px]" data-testid="nav-book-now-btn">
              Book a Car Care
            </button>
          </div>

          <button
            className={`flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${
              scrolled ? "border-black/10 text-nighttext" : "border-white/15 text-paper"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-hamburger"
          >
            {open ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 flex flex-col bg-deep-violet px-6 pt-24 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ background: "linear-gradient(180deg, #1A0E33 0%, #130826 100%)" }}
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                  onClick={() => goSection(l.id)}
                  className="flex items-center justify-between border-b border-white/5 py-4 text-left font-display text-2xl font-semibold text-paper"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                  <ArrowUpRight size={18} className="text-carza-lite" />
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                onClick={() => goPage("/aggregator")}
                className="flex items-center justify-between border-b border-white/5 py-4 text-left font-display text-2xl font-semibold text-mist"
                data-testid="mobile-nav-aggregator"
              >
                CARZA Nearby Platform
                <span className="ml-2 inline-block rounded-full bg-carza/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-carza">Soon</span>
                <ArrowUpRight size={18} className="text-carza-lite" />
              </motion.button>
              <motion.button
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35 }}
                onClick={() => goPage("/investors")}
                className="flex items-center justify-between border-b border-white/5 py-4 text-left font-display text-2xl font-semibold text-mist"
                data-testid="mobile-nav-investors"
              >
                For Investors
                <ArrowUpRight size={18} className="text-carza-lite" />
              </motion.button>
            </nav>
            <div className="mt-auto flex flex-col gap-3 pb-28">
              {!customer && (
                <button onClick={() => goPage("/login")} className="btn btn-outline w-full py-4" data-testid="mobile-nav-signin-btn">
                  Sign in / Get Started
                </button>
              )}
              <button onClick={() => { setOpen(false); openBooking(); }} className="btn btn-primary w-full py-4" data-testid="mobile-nav-book-btn">
                Book a Car Care
              </button>
              <button onClick={() => goPage("/partners")} className="btn btn-outline w-full py-4" data-testid="mobile-nav-partner-btn">
                Become a Partner
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
